// OiPowderOrder endpoints


export interface OiPowderOrderDTO {
	uuid: string;
	jsonData: string;
	createdAt?: string;
	orderId?: string;
	isDraft?: boolean;
	isArchived?: boolean;
	isCancelled?: boolean;
	lastUpdated?: string;
	author?: string;
}


export interface OiPowderOrderDTOMinimal {
	uuid: string;
	orderId?: string;
}

export interface OiPowderOrderGrouped{
	 drafts: OiPowderOrderDTOMinimal[];
  nonDrafts: OiPowderOrderDTOMinimal[];
  cancelled: OiPowderOrderDTOMinimal[];
}


const BASE_URL = `${process.env.REACT_APP_API_URL}/oi-powder-order`;

function getFilenameFromContentDisposition(contentDisposition: string | null, fallback: string): string {
	if (!contentDisposition) {
		return fallback;
	}

	const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
	if (utf8Match?.[1]) {
		return decodeURIComponent(utf8Match[1]);
	}

	const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
	if (asciiMatch?.[1]) {
		return asciiMatch[1];
	}

	return fallback;
}

// Create OiPowderOrder (returns uuid)
// Only send JsonData and IsDraft to match backend OiPowderOrderCreate
export async function CreateOiPowderOrder(payload: string, isDraft: boolean, author: string): Promise<string | null> {
	try {
		const payloadObj = {
			JsonData: payload,
			IsDraft: isDraft,
			Author: author,
		};
		const response = await fetch(`${BASE_URL}/`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payloadObj),
		});
		if (response.ok) {
			console.log("CreateOiPowderOrder response:", response);
			return await response.text(); // Assuming the API returns the uuid as plain text. Adjust if it's JSON.
		}
		return null;
	} catch (error) {
		console.error('Failed to create OiPowderOrder', error);
		return null;
	}
}

// Update OiPowderOrder (PATCH /{uuid})
export async function UpdateOiPowderOrder(uuid: string, payload: string, isDraft: boolean): Promise<boolean> {
	try {
        const payloadObj = {
            JsonData: payload,
            IsDraft: isDraft,
        }
		const response = await fetch(`${BASE_URL}/${uuid}`, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payloadObj),
		});
		return response.ok;
	} catch (error) {
		console.error('Failed to update OiPowderOrder', error);
		return false;
	}
}

// Get paginated OiPowderOrders
export async function getOiPowderOrdersPaginate(page: number, pageSize = 10): Promise<OiPowderOrderDTO[] | null> {
	try {
		const url = `${BASE_URL}/page/${page}?pageSize=${pageSize}`;
		const response = await fetch(url, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		if (response.ok) {
			return await response.json();
		}
		return null;
	} catch (error) {
		console.error('Failed to fetch paginated OiPowderOrders', error);
		return null;
	}
}

// Get OiPowderOrder by uuid
export async function GetOiPowderOrder(uuid: string): Promise<OiPowderOrderDTO | null> {
	try {
		const response = await fetch(`${BASE_URL}/${uuid}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		if (response.ok) {
			return await response.json();
		}
		if (response.status === 404) return null;
		return null;
	} catch (error) {
		console.error('Failed to fetch OiPowderOrder', error);
		return null;
	}
}

export async function getAllOiPowderOrdersGrouped(): Promise<OiPowderOrderGrouped | null> {
	try {
		const response = await fetch(`${BASE_URL}/all-grouped`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (response.ok) {
			const data: OiPowderOrderGrouped = await response.json();
			console.log(data);
			return data;
		}

		return null;
	} catch (error) {
		console.error('Failed to fetch all OiPowderOrder summaries', error);
		return null;
	}
}

export async function getPowderOrdersFiltered(query: string): Promise<OiPowderOrderGrouped | null> {
try {
		const response = await fetch(`${BASE_URL}/all-filtered?search=${encodeURIComponent(query)}`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		if (response.ok) {
			const data: OiPowderOrderGrouped = await response.json();
			console.log(data);
			return data;
		}

		return null;
	} catch (error) {
		console.error('Failed to fetch filtered OiPowderOrder summaries', error);
		return null;
	}
}



export async function getAllOiPowderOrderSummaries(): Promise<OiPowderOrderDTO[] | null> {
	try {
		const response = await fetch(`${BASE_URL}/all`, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		if (response.ok) {
			const data = await response.json();
			console.log(data);
			return data;
		}
		return null;
	} catch (error) {
		console.error('Failed to fetch all OiPowderOrder summaries', error);
		return null;
	}
}
export async function deleteOiPowderOrder(uuid: string): Promise<boolean> {
	try {
		const response = await fetch(`${BASE_URL}/${uuid}`, {
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		return response.ok;
	} catch (error) {
		console.error('Failed to delete OiPowderOrder', error);
		return false;
	}
}

export async function cancelOiPowderOrder(uuid: string): Promise<boolean> {
	try {
		const response = await fetch(`${BASE_URL}/${uuid}/cancel`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
		});
		return response.ok;
	} catch (error) {
		console.error('Failed to cancel OiPowderOrder', error);
		return false;
	}
}

export async function DownloadPowderOrderPdf(uuid: string, fallbackFilename?: string): Promise<boolean> {
	try {
		const response = await fetch(`${BASE_URL}/pdf/${uuid}`, {
			method: 'GET',
			headers: {
				'Accept': 'application/pdf',
			},
		});

		if (!response.ok) {
			console.error('Failed to download OiPowderOrder PDF', response.status, response.statusText);
			return false;
		}

		const blob = await response.blob();
		const filename = getFilenameFromContentDisposition(
			response.headers.get('content-disposition'),
			fallbackFilename || `report-${uuid}.pdf`,
		);

		const blobUrl = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = blobUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(blobUrl);

		return true;
	} catch (error) {
		console.error('Failed to download OiPowderOrder PDF', error);
		return false;
	}
}
